import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getClientCouponInformationByCouponCode } from '../../../crud/payments_api/client_coupons';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import { Download } from 'react-bootstrap-icons';
import { formatCurrency } from '../../../utils/functions';
import './ClientCouponLanding.scss';

export interface CouponData {
  is_valid: boolean;
  coupon_code: string;
  timestamp: string;
  client_username: string | null;
  client_lastname: string | null;
  coupon_name: string;
  coupon_amount: number | null;
  coupon_percentage: number | null;
  isglobal: boolean;
  local_name: string;
  local_id: number;
  local_address: string;
  menu_name: string | null;
  category_name: string | null;
  item_name: string | null;
  expiration_date: string | null;
}

// A confetti explosion component using 100 pieces generated via a loop
const ConfettiExplosion: React.FC = () => {
  return (
    <div className="confetti-container">
      {Array.from({ length: 100 }).map((_, i) => (
        <div key={i} className="confetti-piece"></div>
      ))}
    </div>
  );
};

const ClientCouponLanding: React.FC = () => {
  const { coupon_code } = useParams<{ coupon_code: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [coupon, setCoupon] = useState<CouponData | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const couponRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    if (coupon_code) {
      getClientCouponInformationByCouponCode(coupon_code)
        .then((response: any) => {
          if (response && response.data) {
            setCoupon(response.data);
          }
        })
        .catch((err) => {
          console.error(err);
          setError(true);
        })
        .finally(() => setLoading(false));
    }
  }, [coupon_code]);

  // Trigger confetti explosion when loading finishes and coupon is available
  useEffect(() => {
    if (!loading && !error && coupon) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1500);
    }
  }, [loading, error, coupon]);

  const downloadAsImage = () => {
    if (couponRef.current) {
      html2canvas(couponRef.current, { scale: 2 }).then((canvas) => {
        const link = document.createElement('a');
        link.download = `coupon_${coupon?.coupon_code}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  const renderContent = () => {
    if (!coupon) return null;

    // Determine discount value label
    let discountValue = '';
    if (coupon.coupon_percentage !== null) {
      discountValue = `${coupon.coupon_percentage}%`;
    } else if (coupon.coupon_amount !== null) {
      discountValue = `${formatCurrency(coupon.coupon_amount)}`;
    }

    // Determine discount scope text based on coupon details
    let discountScope = '';
    if (coupon.isglobal) {
      discountScope = 'en cualquier producto';
    } else if (coupon.menu_name) {
      discountScope = 'en todos los productos';
    } else if (coupon.category_name) {
      discountScope = `en todos los productos de la categoría ${coupon.category_name}`;
    } else if (coupon.item_name) {
      discountScope = `en tu próximo ${coupon.item_name}`;
    }

    // Format expiration date if available
    let expirationText = '';
    if (coupon.expiration_date) {
      const expDate = new Date(coupon.expiration_date);
      expirationText = `Válido hasta ${expDate.toLocaleDateString()}`;
    }

    return (
      <div className="coupon-content" ref={couponRef}>
        {showConfetti && <ConfettiExplosion />}
        <div className="coupon-header">
          <h1 className="local-name">{coupon.local_name}</h1>
          <p className="local-address">{coupon.local_address}</p>
        </div>
        <div className="coupon-discount">
          <p className="discount-text">
            Tienes {discountValue} de descuento {discountScope}
          </p>
          {expirationText && <p className="expiration-text">{expirationText}</p>}
        </div>
        <div className="coupon-qr">
          <QRCode value={coupon.coupon_code} size={150} bgColor="#fff" fgColor="#FC7556" />
        </div>
        <div className="coupon-code">
          <p className="code-text"> {coupon.coupon_code}</p>
          </div>
        <div className="coupon-download">
          <button onClick={downloadAsImage} className="download-button">
            Descargar <Download size={20} />
          </button>
        </div>
        <div className="coupon-powered">
          <span>Powered by </span>
          <img
            src={`${process.env.PUBLIC_URL}/logos/Recurso 49MEDIOPUNTO.png`}
            alt="Mediopunto Logo"
            className="powered-logo"
          />
          <span>MedioPunto</span>
        </div>
      </div>
    );
  };

  const renderError = () => (
    <div className="coupon-error">
      <h1 className="error-message">Ups, el cupón ya no se encuentra disponible</h1>
      <div className="error-powered">
        <span>Powered by </span>
        <img
          src={`${process.env.PUBLIC_URL}/logos/Recurso 49MEDIOPUNTO.png`}
          alt="Mediopunto Logo"
          className="powered-logo"
        />
        <span>MedioPunto</span>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="coupon-loading">
      <div className="spinner-container">
        <div className="spinner"></div>
      </div>
      <p className="loading-message">Cargando...</p>
    </div>
  );

  return (
    <div className="client-coupon-landing-container">
      {loading ? renderLoading() : error ? renderError() : renderContent()}
    </div>
  );
};

export default ClientCouponLanding;
